/**
 * Xenial dm data service
 */
import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
} from '@angular/common/http';
import { get } from 'lodash';

import { Observable, observableThrowError, rx } from '@shared/rx';
import {
  IApiListResponse,
  IEventType,
  IFilterQuery,
  Primitive,
} from '@shared/types';
import { environment } from '../../../../environments/environment';
import { lazyInject } from '@app/shared/decorators';
import { getStartOfLocalDayISOString } from '@app/shared/utils';
import { createRequestParams } from '@app/shared/utils/request.utils';
import { buildServiceIdentifyContext } from '@app/shared/utils/http-context.utils';
import { ENTITY_TYPES } from '@app/shared/types/enums/entityTypes.enum';

@Injectable()
export class DmDataService {
  @lazyInject(HttpClient) public http: HttpClient;
  public static UTILITY_TYPE: string = 'DmDataService';
  public sitePreferences = null;
  public companyPreferences = null;
  private get baseUrl(): string {
    return environment.dmBackendURI;
  }

  public getEventTypes(): Observable<IEventType[]> {
    return this.http
      .get(`${this.baseUrl}/event-type/master`, {
        context: buildServiceIdentifyContext(ENTITY_TYPES.EVENT_TYPE),
      })
      .pipe(
        this.catchHttpErrorAndPickErrorList(),
        rx.map((response: IApiListResponse<IEventType>) =>
          get(response, 'items', []),
        ),
      );
  }

  public getSitePreferences(): Observable<IEventType[]> {
    return this.http
      .get(`${this.baseUrl}/preferences/site`)
      .pipe(this.catchHttpErrorAndPickErrorList());
  }

  public fetchCompanyPreferences(): Observable<any> {
    return this.http
      .get(`${this.baseUrl}/preferences/company`)
      .pipe(this.catchHttpErrorAndPickErrorList());
  }

  /**
   * Fetch theme data for a terminal (by id). Extracts param construction for clarity & reuse.
   */
  public fetchThemeByAppCode(terminalId: string | number) {
    const query = this.createParams({
      top: 1,
      select: ['terminal_scheme.color_theme'],
      filter: `entity_id eq in(${terminalId}) and is_master eq in(true,false)`,
      include_mappings: false,
      include_entities: ['terminal-scheme'],
      effectiveDate: getStartOfLocalDayISOString(),
    });
    const url = `${this.baseUrl}/terminal/current`;
    return this.http
      .get(url, { params: query })
      .pipe(this.catchHttpErrorAndPickErrorList());
  }

  public getDictionaryCode(
    appName: string,
    appVersion: string,
    dictionaryParams?: IFilterQuery,
  ): Observable<Record<string, Object | Primitive>> {
    return this.http.get<Record<string, Primitive | Object>>(
      `${this.baseUrl}/dictionary/${appName}/build/${appVersion}`,
      {
        params: this.createParams(dictionaryParams as unknown as IFilterQuery),
      },
    );
  }

  public createParams(query: IFilterQuery = {}): HttpParams {
    return createRequestParams({
      ...query,
    });
  }

  private catchHttpErrorAndPickErrorList =
    (): any =>
    <T>(input$: Observable<T>): Observable<T> =>
      input$.pipe(
        rx.catchError(({ error }: HttpErrorResponse) =>
          observableThrowError(get(error, 'errors')),
        ),
      );
}
